import { h } from 'preact';
import Store from 'store/store';

import { del as apiDelete } from 'utils/api';
import {
	duplicateTitle,
	duplicateSlug,
	IMDBIdValidationError,
	missingHideDate,
	missingHomeEntDate,
	missingReleaseDate,
	missingSlug,
	missingShowtimesSource,
	missingTitle,
	urlValidationError,
	validateSlug
} from 'utils/errors';
import * as jsonPointer from 'utils/jsonPointer';
import { alert, confirm, toast } from 'utils/modal';
import { getReleaseMessaging } from 'utils/releaseMessaging';
import { canUser } from 'utils/roles';
import slugify from 'utils/slugify';
import { getCurrentMovieUrl } from 'utils/studioConfig';

import Button from 'components/button/button';
import MovieEditor from 'components/editor/movies/movieEditor';
import SimpleField from 'components/editor/settings/simpleField';
import DateField from 'components/editor/settings/dateField';
import ShowtimesField from 'components/editor/settings/showtimesField';

import s from 'components/editor/movies/movieInfo/movieInfo.sss';

export default class MovieInfoEditor extends MovieEditor {
	s = s;

	get basePath() {
		return this.props.dataRoot || '';
	}

	get title() {
		return 'Movie info';
	}

	constructor(props) {
		super(props);

		this.onDeletePlaceholderClick = this.onDeletePlaceholderClick.bind(this);
		this.slugMatchesTitle = this.slugMatchesTitle.bind(this);
		this.transformToSlug = this.transformToSlug.bind(this);
	}

	// ===============
	// Event handlers
	// ===============

	async onDeletePlaceholderClick() {
		const ok = await confirm('This will delete this placeholder movie and cannot be undone', { title: 'Delete placeholder movie?', danger: true, button: 'Delete' });
		if (!ok) return;

		try {
			const id = this.props.data?.id;
			const response = await apiDelete(`/movies/${id}`, {});
			if (response.success) {
				const storeData = Store.get();

				const movies = jsonPointer.get(storeData.data, '/movies') || {};
				const savedMovies = jsonPointer.get(storeData.savedData, '/movies') || {};

				const updatedMovies = { ...movies };
				const updatedSavedMovies = { ...savedMovies };
				delete updatedMovies[id];
				delete updatedSavedMovies[id];

				jsonPointer.set(storeData.data, '/movies', updatedMovies);
				jsonPointer.set(storeData.savedData, '/movies', updatedSavedMovies);

				// Redirect to movies list
				window.location.hash = `#movies`;
				toast('Placeholder movie deleted', { icon: 'delete' });
			}
		} catch (err) {
			console.error('Error deleting movie', err);
			alert('Sorry, an error occurred while deleting this movie', { title: 'Error' });
		}
	}

	// ===============
	// Helpers
	// ===============

	slugMatchesTitle() {
		const localeTitle = jsonPointer.get(this.props.data, `/content/title`);
		const globalTitle = jsonPointer.get(this.props.data, `/base_movie/content/title`);

		const titleAsSlug = slugify(localeTitle || globalTitle || '', { removeDiacritics: true });
		const currentSlug = jsonPointer.get(this.props.data, `/content/slug`);
		return !currentSlug || currentSlug === titleAsSlug;
	}

	transformToSlug(text) {
		const title = text || jsonPointer.get(this.props.data, `/base_movie/content/title`) || '';
		return slugify(title, { removeDiacritics: true });
	}

	// ================
	// Fields
	// ================

	renderTitle(opts = {}) {
		return (
			<SimpleField
				key="title"
				label="Movie title"
				path={`${this.basePath}/content/title`}
				error={missingTitle(this.props.data) || duplicateTitle(this.props.data)}
				alsoSet={[{
					path: `${this.basePath}/content/slug`,
					method: this.transformToSlug,
					condition: this.slugMatchesTitle
				}]}
				{...opts}
			/>
		);
	}

	renderReleaseDate(opts = {}) {
		return (
			<DateField
				key="release"
				label="Release date"
				path={`${this.basePath}/content/dates/release`}
				help="The date the movie releases"
				error={missingReleaseDate(this.props.data)}
				allowCustomPrecision
				{...opts}
			/>
		);
	}

	renderTicketingDate(opts = {}) {
		return (
			<DateField
				key="ticketing"
				label="Ticketing date"
				path={`${this.basePath}/content/dates/ticketing`}
				help="The date that tickets go on sale"
				{...opts}
			/>
		);
	}

	renderHomeEntDate(opts = {}) {
		if (!this.homeEntEnabled) return null; // Only show if homeEnt is enabled
		return (
			<DateField
				key="homeent"
				label="Home Entertainment date"
				path={`${this.basePath}/content/dates/homeEnt`}
				help="The date that the movie is available for home entertainment"
				error={missingHomeEntDate(this.props.data)}
				{...opts}
			/>
		);
	}

	renderReleaseMessaging(opts = {}) {
		return (
			<SimpleField
				key="releaseMessaging"
				label="Release messaging"
				path={`${this.basePath}/content/releaseMessagingOverride`}
				placeholder={getReleaseMessaging(this.movie, this.movie?.status, this.state.thundrData?.copy?.baseCopy)}
				help="Auto-generated based on movie status and dates. Add text to override."
				{...opts}
			/>
		);
	}

	renderHideFromCarousels(opts = {}) {
		return (
			<DateField
				key="hideFromCarouselsAfter"
				label='Hide from "In Release" and "Coming Soon" after date'
				path={`${this.basePath}/content/dates/hideFromCarouselsAfter`}
				error={missingHideDate(this.props.data)}
				toggleable
				{...opts}
			/>
		);
	}

	renderSlug(opts = {}) {
		// onInput func: transform input to slug format
		// props.onInput will run before the internal onInput logic
		const transform = (e) => {
			const { selectionStart, selectionEnd, value } = e.target;

			const newValue = slugify(value, { removeDiacritics: true, allowEdgeDashes: true });
			const lengthDiff = newValue.length - value.length;

			e.target.value = newValue;
			e.target.setSelectionRange(selectionStart + lengthDiff, selectionEnd + lengthDiff); // Move cursor to correct position after transformation
		};

		return (
			<SimpleField
				key="slug"
				class={this.slugMatchesTitle() ? this.s.greyedOut : ''}
				label="Movie URL"
				path={`${this.basePath}/content/slug`}
				preText={getCurrentMovieUrl()}
				error={duplicateSlug(this.props.data) || validateSlug(this.props.data) || missingSlug(this.props.data)}
				onInput={transform}
				{...opts}
			/>
		);
	}

	renderExternalUrl(opts = {}) {
		return (
			<SimpleField
				key="externalUrl"
				type="url"
				label="Use external URL"
				path={`${this.basePath}/content/url`}
				placeholder="https://www.example.com/"
				error={urlValidationError(this.props.data)}
				help="Sets a redirect to the specified URL"
				toggleable
				{...opts}
			/>
		);
	}

	renderShowtimesSource(opts = {}) {
		const isShowtimesLive = this.movie.pages?.showtimes?.active;

		return (
			<ShowtimesField
				key="showtimes"
				label="Showtimes source"
				path={`${this.basePath}/content/showtimesId`}
				help="Select a movie from our database to add showtime info"
				error={missingShowtimesSource(this.props.data)}
				allowClear={!isShowtimesLive}
				{...opts}
			/>
		);
	}

	renderGenre() {
		return (
			<SimpleField
				key="genre"
				label="Frontend genre"
				path={`${this.basePath}/content/genre`}
				placeholder="Genre (e.g. Comedy)"
				help="The genre shown on the public website"
			/>
		);
	}

	renderMagicGenre(opts = {}) {
		return (
			<SimpleField
				key="magicGenre"
				label="Magic genre"
				path={`${this.basePath}/content/dataLayer/magicGenre`}
				placeholder="Genre (e.g. Comedy)"
				help="Used for dataLayers"
				{...opts}
			/>
		);
	}

	renderEssenceGenre(opts = {}) {
		return (
			<SimpleField
				key="essenceGenre"
				label="Essence genre"
				path={`${this.basePath}/content/dataLayer/essenceGenre`}
				placeholder="Genre (e.g. Comedy)"
				help="Used for dataLayers"
				{...opts}
			/>
		);
	}

	renderIMDBId(opts = {}) {
		return (
			<SimpleField
				key="imdbId"
				label="IMDB ID"
				path={`${this.basePath}/content/dataLayer/imdbId`}
				placeholder='Must be in the format of "tt1234567"'
				error={IMDBIdValidationError(this.props.data)}
				help="Used for dataLayers"
				{...opts}
			/>
		);
	}

	// ===============
	// Actions
	// ===============

	renderDeletePlaceholder(opts = {}) {
		return (
			<Button
				key="delete"
				disabled={!canUser('publish')}
				onClick={this.onDeletePlaceholderClick}
				danger
				outline
				{...opts}
			>
				Delete placeholder
			</Button>
		);
	}

	// ===================
	// Field renderers
	// ===================

	renderFieldsPlaceholder() {}
	renderFieldsMoviePlatform() {}

	renderFields() {
		if (this.movie.placeholder) return this.renderFieldsPlaceholder();
		return this.renderFieldsMoviePlatform();
	}

	// ===================
	// dataLayer renderers
	// ===================

	renderDataLayerFields() {
		return [
			this.renderMagicGenre(),
			this.renderEssenceGenre(),
			this.renderIMDBId(),
		];
	}

	renderDataLayerSectionHeader() {
		return (
			<div class={this.s.header}>
				<h3>dataLayer Settings</h3>
				<div class={this.s.description}>
					<span>These variables are required for additional tagging and Essence tracking. They can be obtained </span>
					<a href="https://lookerstudio.google.com/u/0/reporting/58e6e2c1-904e-45a4-be8f-408ae32c16d1/page/LzXPC" target="_blank" rel="noopener noreferrer">here</a>
					<span>. To request access, please contact </span>
					<a href="mailto:Callum.Wood@nbcuni.com" target="_blank" rel="noopener noreferrer">Callum.Wood@nbcuni.com</a>
					<span>.</span>
				</div>
			</div>
		);
	}

	renderDataLayerSettings() {
		if (this.movie.placeholder) return null;
		return (
			<div class={this.s.section}>
				{this.renderDataLayerSectionHeader()}
				<div class={this.s.fields}>
					{this.renderDataLayerFields()}
				</div>
			</div>
		);
	}

	// ===================
	// Action renderers
	// ===================
	
	renderActionsPlaceholder() {}
	renderActionsMoviePlatform() {}

	renderActions() {
		if (this.movie.placeholder) return this.renderActionsPlaceholder();
		return this.renderActionsMoviePlatform();
	}

	// ===================
	// Main renderers
	// ===================

	renderDescription() {
		return [
			'Controls info related to the movie.',
			'Changes here will be reflected on both the hubsite and movie pages, unless overridden in the page editor.'
		].map(str => <p>{str}</p>);
	}

	renderHeader() {
		const description = this.renderDescription();

		return (
			<div class={this.s.header}>
				<h2>{this.title || 'Movie info'}</h2>
				{description && (<div class={this.s.description}>{description}</div>)}
			</div>
		);
	}

	renderVisual() {
		return (
			<div class={this.s.visual}>
				{this.renderHeader()}
				<div class={this.s.fields}>
					{this.renderFields()}
				</div>
				{this.renderDataLayerSettings()}
				<div class={this.s.actions}>
					{this.renderActions()}
				</div>
			</div>
		);
	}

	renderSettings() {}
}